import type { PaginatedResponse } from "@/types/common.types";

// 课程状态：1-正常，2-停用
export type CourseStatus = 1 | 2;

export interface Course {
  id: string;
  articleId: string;
  title: string;
  lansiReal: number; // 真实蓝思值
  lansiOvert: number; // 展示蓝思值
  articleLen: number; // 文章长度
  level: string; // 级别
  coverUrl: string; // 封面图片地址
  words: string; // 单词列表，逗号分隔
  isFiction: number; // 是否虚构，1-是，0-否
  topic: string; // 主题
  topicCode: string; // 主题代码
  videoUrl: string; // 视频地址
  lansiRealShow: string; // 课程蓝思值
  lansiOvertShow: string; // 课程蓝思值
  videoLength: number; // 视频长度（毫秒）
  duration: number; // 学习时间（毫秒）
  toalDuration: number; // 总学习时间（毫秒）
  desc: string; // 描述
  status: CourseStatus; // 状态：1-正常，0-停用
  stoped_at: string; // 停用时间
  stopedReasion: string; // 停用理由
  uuid: string;
  created_at: string; // 创建时间
  updated_at: string; // 更新时间
  created_by: string; // 创建人
  updated_by: string; // 更新人
}

export interface CourseDetailType {
  id: string;
  articleId: string;
  title: string;
  titleMp3: string;
  picture: string;
  articleLen: number; // 文章长度
  level: string; // 级别
  isFiction: number; // 是否虚构，1-是，0-否
  theme: string; // 主题
  themeCode: string; // 主题代码
  videoUrl: string; // 视频地址
  videoLength: number; // 视频长度（毫秒）
  duration: number; // 学习时间（毫秒）
  toalDuration: number; // 总学习时间（毫秒）
  blueScore: string; // 蓝思值
  blueScoreDesc: string;
  wordCount: number; // 单词数
  coreWord: CoreWord[]; // 学新词
  animation: {
    // 看动画
    Video_length: number;
    video_url: string;
    subtitle: animationSubtitle[];
  };
  audio: {
    audio_url: string;
    audio_length: number;
    subtitle: audioSubtitle[];
  };
  analysisTerms: analysisTermsType[];
  annotationTerms: annotationTermsType[];
  articleQuestions: articleQuestionsType[];
  extensionSkills: extensionSkillType[];
  learnGrammar: grammar[];
  practiceSpeaking: speaking[];
}

export type CoursesResponse = PaginatedResponse<Course>;
export type CoreWord = {
  article_id: string;
  definition_cn: string;
  example_sentence_cn: string;
  example_sentence_en: string;
  example_sentence_mp3: string;
  id: string;
  phonetic: string;
  phonetic_audio_us: string;
  pic_name: string;
  pos: string;
  sentence_from_text: string;
  vocab: string;
  vocab_level: string;
  vovabulary_id: string;
  word: string;
};
export type animationSubtitle = {
  en: string;
  endTime: number;
  id: number;
  startTime: number;
  type: string;
  zh: string;
};
export type audioSubtitle = {
  en: string;
  endTime?: number;
  id: number;
  lineBreak?: number;
  startTime?: number;
  text: string;
  type: string;
  zh: string;
};
export type analysisTermsType = {
  article_id: string;
  definition_cn: string;
  desc: string;
  explanation: string;
  id: string;
  pos: string;
  sentence_from_text: string;
  term: string;
};
export type annotationTermsType = {
  article_id: string;
  definition_cn: string;
  desc: string;
  id: string;
  pos: string;
  sentence_from_text: string;
  word: string;
};
export type articleQuestionsType = {
  answer: string;
  article_id: string;
  assessment_area_primary: string;
  assessment_area_secondary: string;
  explanation: string;
  id: string;
  question_id: string;
  question_stem_en: string;
  question_stem_en_play: string;
  question_stem_zh: string;
  options: articleQuestionOptionType[];
};
export type articleQuestionOptionType = {
  id: string;
  option: string;
  title: string;
};
type extensionSkillType = {
  article_id: string;
  content: string;
  field_capabilities: string;
  id: string;
  level: string;
  strategy_introduce: string;
  strategy_name: string;
  question: matchQuestionType[] | sortQuestionType[] | chooseQuestionType[];
};
export type matchQuestionType = {
  id: string;
  match_type: string;
  question_id: string;
  topic: string;
  type: string;
  answer: {
    answer: string;
    option: string;
  }[];
  options: matchOptionType[];
};
type matchOptionType = {
  answer: string;
  id: string;
  new_answer: string;
  option: string;
  title: string;
};
export type sortQuestionType = {
  id: string;
  question_id: string;
  topic: string;
  type: string;
  options: sortOptionType[];
};
type sortOptionType = {
  answer: string;
  id: string;
  option: string;
  title: string;
};
export type chooseQuestionType = {
  id: string;
  question_id: string;
  topic: string;
  type: string;
  options: chooseOptionType[];
};
export type chooseOptionType = {
  answer: string;
  id: string;
  option: string;
  title: string;
};
type grammar = {
  Introduction: string;
  analysis: string;
  article_id: string;
  code: string;
  id: string;
  knowledge: string;
  question: grammarQuestionType[];
};
export type grammarQuestionType = {
  analysis: string;
  answer: string;
  question_cn: string;
  question_en: string;
  question_id: string;
  options: grammarOptionType[];
};
export type grammarOptionType = {
  id: string;
  key: string;
  value: string;
};
type speaking = {
  id: string;
  article_id: string;
  pic_name: string;
  question_en: string;
  question_zh: string;
  question_id: string;
  question_mp3: string;
  sample_answer: string;
  sample_answer_mp3: string;
  sentence_frame: string;
  type: string;
};

export type GetCoursesParams = {
  page: number;
  page_size: number;

  // 查询条件
  title?: string; // 课程名称

  // 状态筛选
  status?: CourseStatus | "all";

  // 蓝思值范围
  lansi_min?: number;
  lansi_max?: number;
};

// 用于状态下拉框的选项
export const STATUS_OPTIONS = [
  { value: "all", label: "全部" },
  { value: "1", label: "正常" },
  { value: "2", label: "停用" },
];

// 课程详情模块信息
export type CourseDetailPage =
  | "base"
  | "core"
  | "animation"
  | "audio"
  | "annotation"
  | "analysis"
  | "skills"
  | "articleAndTest"
  | "grammerAndTest"
  | "speaking";

// 更新课程参数
export type saveCourseParams = {
  article_id: string; // 课程ID
  cate: CourseDetailPage; // 变更模块
  field: string; // 修改字段
  value: string | string[]; // 修改值
  option_field?: string; // 选项修改字段
  option_id?: string; // 选项ID
  question_field?: string; // 题目修改字段
  question_id?: string; // 题目ID
  sub_id?: string; // 分类ID
  operation?: "add" | "delete";
};
